import React, { useEffect, useState } from "react";
import integrationsService from "../../../services/integrationsService";
import LoadingSpinner from "../../common/LoadingSpinner";
import { formatInUserTimezone } from "../../../utils/timezone";

function ConflictsTab() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await integrationsService.listConflicts();
      setItems(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to load conflicts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const resolveOne = async (id, resolution) => {
    setBusy(true);
    try {
      await integrationsService.resolveConflict(id, resolution);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to resolve");
    } finally {
      setBusy(false);
    }
  };

  const resolveAll = async (resolution) => {
    if (!window.confirm(`Resolve all conflicts as "${resolution}"?`)) return;
    const ids = items.map((i) => i.id);
    setBusy(true);
    try {
      await integrationsService.bulkResolveConflicts(ids, resolution);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || "Bulk resolve failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <LoadingSpinner message="Loading conflicts..." />;

  return (
    <div>
      <div className="management-header">
        <div>
          <h2>Conflict Queue</h2>
          <p className="text-muted">
            Fields where CRM and external system disagree. CRM wins by default.
          </p>
        </div>
        {items.length > 0 && (
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="btn btn-secondary"
              onClick={() => resolveAll("crm")}
              disabled={busy}
            >
              <i className="fas fa-check" /> Resolve all as CRM
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => resolveAll("ignore")}
              disabled={busy}
            >
              <i className="fas fa-eye-slash" /> Ignore all
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-circle" /> {error}
        </div>
      )}

      {items.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-check-circle" />
          <h3>No pending conflicts</h3>
          <p>Every sync is in agreement.</p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Lead</th>
                <th>Field</th>
                <th>CRM Value</th>
                <th>External Value</th>
                <th>Source</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id}>
                  <td>
                    <a href={`/leads/${c.lead_id}`} target="_blank" rel="noreferrer">
                      {c.lead_company || `Lead #${c.lead_id}`}
                    </a>
                  </td>
                  <td><code>{c.field}</code></td>
                  <td>{c.crm_value || <em className="text-muted">empty</em>}</td>
                  <td>{c.external_value || <em className="text-muted">empty</em>}</td>
                  <td>{c.external_source}</td>
                  <td>{formatInUserTimezone(c.created_at)}</td>
                  <td className="actions">
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => resolveOne(c.id, "crm")}
                      disabled={busy}
                    >
                      Keep CRM
                    </button>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => resolveOne(c.id, "external")}
                      disabled={busy}
                    >
                      Accept External
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => resolveOne(c.id, "ignore")}
                      disabled={busy}
                    >
                      Ignore
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ConflictsTab;